import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { setAttribute } from 'components/Form/shared/setAttribute';
import { CourtMartialChargeItemModel } from 'forms/sections/military/disciplinary/types';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { CommonOnError, CommonRequired, CommonScrollIntoView } from 'types/props';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import { CheckboxModel, DateModel, DateRangeModel } from 'forms/shared/types';
import Branch from 'components/Form/Branch';
import DateRange from 'components/Form/DateRange';
import { formIncludesPvqPartC } from 'helpers/form';
import { disciplineSentenceOptions } from 'constants/enums/militaryOptions';
import Checkbox from 'components/Form/Checkbox';

interface Props extends ValidationElementProps, Partial<CourtMartialChargeItemModel> {
  attribute?: string;
  bind?: boolean;
  formType?: string;
  name?: string;
  onError?: CommonOnError;
  onUpdate: (value: Partial<CourtMartialChargeItemModel>) => void;
  required?: CommonRequired;
  scrollIntoView?: CommonScrollIntoView;
}

export default class CourtMartialCharge extends ValidationElement<Props> {
  static defaultProps: Props = {
    ConsequencesExplanation: {} as CourtMartialChargeItemModel['ConsequencesExplanation'],
    CourtMartialChargeDescription: {} as CourtMartialChargeItemModel['CourtMartialChargeDescription'],
    Has: {} as CourtMartialChargeItemModel['Has'],
    ImprisonedWhen: {} as CourtMartialChargeItemModel['ImprisonedWhen'],
    OutcomeConsequences: {} as CourtMartialChargeItemModel['OutcomeConsequences'],
    OutcomeExplanation: {} as CourtMartialChargeItemModel['OutcomeExplanation'],
    OutcomeType: {} as CourtMartialChargeItemModel['OutcomeType'],
    ParoleGranted: {} as CourtMartialChargeItemModel['ParoleGranted'],
    ParoleWhen: {} as CourtMartialChargeItemModel['ParoleWhen'],
    // non-Model properties below
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onUpdate: (value: Partial<CourtMartialChargeItemModel>) => {},
  };

  update = (queue: Partial<CourtMartialChargeItemModel>) => {
    this.props.onUpdate({
      ConsequencesExplanation: this.props.ConsequencesExplanation,
      CourtMartialChargeDescription: this.props.CourtMartialChargeDescription,
      Has: this.props.Has,
      ImprisonedWhen: this.props.ImprisonedWhen,
      OutcomeConsequences: this.props.OutcomeConsequences,
      OutcomeExplanation: this.props.OutcomeExplanation,
      OutcomeType: this.props.OutcomeType,
      ParoleGranted: this.props.ParoleGranted,
      ParoleWhen: this.props.ParoleWhen,
      ...queue,
    });
  };

  updateConsequencesExplanation = (consequencesExplanation: CourtMartialChargeItemModel['ConsequencesExplanation']) => {
    this.update({
      ConsequencesExplanation: consequencesExplanation,
    });
  };

  updateCourtMartialChargeDescription = (courtMartialChargeDescription: CourtMartialChargeItemModel['CourtMartialChargeDescription']) => {
    this.update({
      CourtMartialChargeDescription: courtMartialChargeDescription,
    });
  };

  updateOutcomeConsequences = (consequenceCheckboxModel: CheckboxModel) => {
    const { checked, value } = consequenceCheckboxModel;

    const values: string[] = (this.props.OutcomeConsequences?.values || []).filter((consequence) => consequence !== value);

    const queue: Partial<CourtMartialChargeItemModel> = {
      OutcomeConsequences: { values },
    };

    if (checked) {
      values.push(value);
    } else if (value === 'Imprisonment') {
      CourtMartialCharge.clearFieldsPvqPartC(queue);
    } else if (value === 'Other') {
      queue.ConsequencesExplanation = {} as CourtMartialChargeItemModel['ConsequencesExplanation'];
    }

    this.update(queue);
  };

  updateOutcomeExplanation = (outcomeExplanation: CourtMartialChargeItemModel['OutcomeExplanation']) => {
    this.update({
      OutcomeExplanation: outcomeExplanation,
    });
  };

  updateImprisonedWhen = (imprisonedWhen: CourtMartialChargeItemModel['ImprisonedWhen']) => {
    this.update({
      ImprisonedWhen: imprisonedWhen,
    });
  };

  updateOutcomeType = (outcomeType: CourtMartialChargeItemModel['OutcomeType']) => {
    const queue: Partial<CourtMartialChargeItemModel> = {
      OutcomeType: outcomeType,
    };

    queue.OutcomeConsequences = {} as CourtMartialChargeItemModel['OutcomeConsequences'];

    queue.ConsequencesExplanation = {} as CourtMartialChargeItemModel['ConsequencesExplanation'];

    CourtMartialCharge.clearFieldsPvqPartC(queue);

    if (this.props.OutcomeExplanation?.value && outcomeType?.value !== 'Other') {
      queue.OutcomeExplanation = {} as CourtMartialChargeItemModel['OutcomeExplanation'];
    }

    this.update(queue);
  };

  updateParoleGranted = (paroleGranted: CourtMartialChargeItemModel['ParoleGranted']) => {
    const queue: Partial<CourtMartialChargeItemModel> = {
      ParoleGranted: paroleGranted,
    };

    if (paroleGranted?.value !== 'Yes') {
      queue.ParoleWhen = {} as CourtMartialChargeItemModel['ParoleWhen'];
    }

    this.update(queue);
  };

  updateParoleWhen = (paroleWhen: CourtMartialChargeItemModel['ParoleWhen']) => {
    this.update({
      ParoleWhen: paroleWhen,
    });
  };

  render() {
    return (
      <>
        {/* PVQ: What were your charges under the Uniform Code of [...] */}
        <Field
          attribute={setAttribute(this.props.attribute, 'CourtMartialChargeDescription')}
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('military.pvq.disciplinary.heading.courtmartialchargedescription')}
        >
          <Text
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props.CourtMartialChargeDescription}
            attribute={setAttribute(this.props.attribute, 'CourtMartialChargeDescription')}
            className="court-martial-charge-description"
            name="CourtMartialChargeDescription"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            onUpdate={this.updateCourtMartialChargeDescription}
            required={this.props.required}
          />
        </Field>
        {/* PVQ: What was the outcome for this charge? */}
        <Field
          attribute={setAttribute(this.props.attribute, 'OutcomeType')}
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('military.pvq.disciplinary.heading.outcometype')}
        >
          <RadioGroup
            className="outcome-type option-list option-list-vertical"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={this.props.OutcomeType?.value}
          >
            <Radio
              className="outcome-type-notguilty"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.notguilty')}
              name="outcome-type-notguilty"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="Not guilty"
            />
            <Radio
              className="outcome-type-nolleprosequi"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.nolleprosequi')}
              name="outcome-type-nolleprosequi"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="NolleProsequi"
            />
            <Radio
              className="outcome-type-chargedismissed"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.chargedismissed')}
              name="outcome-type-chargedismissed"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="ChargeDismissed"
            />
            <Radio
              className="outcome-type-chargedismissedafterdeferred"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.chargedismissedafterdeferred')}
              name="outcome-type-chargedismissedafterdeferred"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="ChargeDismissedAfterDeferred"
            />
            <Radio
              className="outcome-type-guilty"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.guilty')}
              name="outcome-type-guilty"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="Guilty"
            />
            <Radio
              className="outcome-type-ontrial"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.ontrial')}
              name="outcome-type-ontrial"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="OnTrial"
            />
            <Radio
              className="outcome-type-awaitingtrial"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.awaitingtrial')}
              name="outcome-type-awaitingtrial"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="AwaitingTrial"
            />
            <Radio
              className="outcome-type-other"
              label={i18n.t('military.pvq.disciplinary.label.outcometype.other')}
              name="outcome-type-other"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateOutcomeType}
              value="Other"
            />
          </RadioGroup>
          {this.props.OutcomeType?.value === 'Other' && (
            <>
              {/* PVQ: Please explain. */}
              <Field
                adjustFor="textarea"
                attribute={setAttribute(this.props.attribute, 'OutcomeExplanation')}
                scrollIntoView={this.props.scrollIntoView}
                title={i18n.t('military.pvq.disciplinary.label.pleaseexplain')}
              >
                <Textarea
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...this.props.OutcomeExplanation}
                  attribute={setAttribute(this.props.attribute, 'OutcomeExplanation')}
                  className="outcome-explanation"
                  name="OutcomeExplanation"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateOutcomeExplanation}
                  required={this.props.required}
                />
              </Field>
            </>
          )}
        </Field>
        {/* PVQ: What were the consequences of this disciplinary action? [...] */}
        <Field
          attribute={setAttribute(this.props.attribute, 'OutcomeConsequences')}
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('military.pvq.disciplinary.heading.outcomeconsequences')}
        >
          <CheckboxGroup
            className="outcome-consequences option-list option-list-vertical"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={this.props.OutcomeConsequences?.values}
          >
            {disciplineSentenceOptions.map((option) => (
              <Checkbox
                className={`outcome-consequences-${option.key}`}
                label={i18n.t(`military.pvq.disciplinary.label.consequences.${option.key}`)}
                key={`${option.key}`}
                name={`outcome-consequences-${option.key}`}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateOutcomeConsequences}
                value={`${option.value}`}
              />
            ))}
          </CheckboxGroup>
          {this.props.OutcomeConsequences?.values?.includes('Other') && (
          <>
            {/* PVQ: Please explain. */}
            <Field
              adjustFor="textarea"
              attribute={setAttribute(this.props.attribute, 'ConsequencesExplanation')}
              scrollIntoView={this.props.scrollIntoView}
              title={i18n.t('military.pvq.disciplinary.label.pleaseexplain')}
            >
              <Textarea
                  // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.ConsequencesExplanation}
                attribute={setAttribute(this.props.attribute, 'ConsequencesExplanation')}
                className="consequences-explanation"
                name="ConsequencesExplanation"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateConsequencesExplanation}
                required={this.props.required}
              />
            </Field>
          </>
          )}
        </Field>
        {formIncludesPvqPartC(this.props.formType)
          && this.props.OutcomeType?.value === 'Guilty'
          && this.props.OutcomeConsequences?.values?.includes('Imprisonment') && (
          <>

            {/* PVQ: When were you imprisoned? */}
            <Field
              adjustFor="daterange"
              attribute={setAttribute(this.props.attribute, 'ImprisonedWhen')}
              shrink
              scrollIntoView={this.props.scrollIntoView}
              title={i18n.t('military.pvq.disciplinary.heading.imprisonedwhen')}
            >
              {/* PVQ: From (Month/Year) */}
              {/* PVQ: To (Month/Year) */}
              <DateRange
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.ImprisonedWhen}
                allowPresent={false}
                attribute={setAttribute(this.props.attribute, 'ImprisonedWhen')}
                className="imprisoned-when"
                fromDateLabel={i18n.t('military.pvq.disciplinary.label.imprisonedwhen.fromdate')}
                hideFromDay
                hideToDay
                name="ImprisonedWhen"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateImprisonedWhen}
                required={this.props.required}
                toDateLabel={i18n.t('military.pvq.disciplinary.label.imprisonedwhen.todate')}
                usePvqFromDate
                usePvqToDate
              />
            </Field>
            {/* PVQ: Were you granted parole or supervised release? */}
            <Branch
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.ParoleGranted}
              attribute={setAttribute(this.props.attribute, 'ParoleGranted')}
              className="parole-granted"
              label={i18n.t('military.pvq.disciplinary.heading.parolegranted')}
              labelSize="h4"
              name="ParoleGranted"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateParoleGranted}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              warning={CourtMartialCharge.hasDataDateRangeModel(this.props.ParoleWhen)}
            />
            {this.props.ParoleGranted?.value === 'Yes' && (
              <>
                {/* PVQ: What are or were the dates of your parole or supervised release? */}
                <Field
                  adjustFor="daterange"
                  attribute={setAttribute(this.props.attribute, 'ParoleWhen')}
                  shrink
                  scrollIntoView={this.props.scrollIntoView}
                  title={i18n.t('military.pvq.disciplinary.heading.parolewhen')}
                >
                  {/* PVQ: From (Month/Year) */}
                  {/* PVQ: To (Month/Year) */}
                  <DateRange
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...this.props.ParoleWhen}
                    attribute={setAttribute(this.props.attribute, 'ParoleWhen')}
                    className="parole-when"
                    fromDateLabel={i18n.t('military.pvq.disciplinary.label.parolewhen.fromdate')}
                    hideFromDay
                    hideToDay
                    name="ParoleWhen"
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    onError={this.props.onError}
                    onUpdate={this.updateParoleWhen}
                    required={this.props.required}
                    toDateLabel={i18n.t('military.pvq.disciplinary.label.parolewhen.todate')}
                    usePvqFromDate
                    usePvqToDate
                  />
                </Field>
              </>
            )}
          </>
        )}
      </>
    );
  }

  private static clearFieldsPvqPartC(
    queue: Partial<CourtMartialChargeItemModel>
  ): void {
    // eslint-disable-next-line no-param-reassign
    queue.ImprisonedWhen = {} as CourtMartialChargeItemModel['ImprisonedWhen'];

    // eslint-disable-next-line no-param-reassign
    queue.ParoleGranted = {} as CourtMartialChargeItemModel['ParoleGranted'];

    // eslint-disable-next-line no-param-reassign
    queue.ParoleWhen = {} as CourtMartialChargeItemModel['ParoleWhen'];
  }

  private static hasDataDateModel(dateModel?: DateModel): boolean {
    return Boolean(dateModel?.month || dateModel?.year);
  }

  private static hasDataDateRangeModel(dateRangeModel?: DateRangeModel): boolean {
    return CourtMartialCharge.hasDataDateModel(dateRangeModel?.from)
      || CourtMartialCharge.hasDataDateModel(dateRangeModel?.to);
  }
}
